import React, { useState, useEffect } from "react";
import {
    Modal,
    Button,
    Upload,
    Input,
    Form,
    Steps,
    Table,
    notification,
    Result,
    InputNumber,
    Space,
    Tag,
    Descriptions,
    Select,
    Radio,
} from "antd";
import {
    UploadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    LoadingOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import { httpGet, httpPost, httpPostFormData, customBaseUrl } from "../../../store/http";
import { currencyFormat } from "../../../utils/helper";

const { Option } = Select;
const { Step } = Steps;

const BulkDisbursementModal = ({ showModal, hideModal, selectedAccount, allAccounts, onSuccess, initialBatchRef }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [batchRef, setBatchRef] = useState(null);
    const [batchData, setBatchData] = useState(null);
    const [file, setFile] = useState(null);
    const [narration, setNarration] = useState("Bulk Disbursement");
    const [otp, setOtp] = useState("");
    const [polling, setPolling] = useState(false);
    const [manualAccount, setManualAccount] = useState(null);
    const [filterStatus, setFilterStatus] = useState("ALL");

    useEffect(() => {
        if (showModal && initialBatchRef) {
            resumeBatch(initialBatchRef);
        }
    }, [showModal, initialBatchRef]);

    const resumeBatch = async (ref) => {
        setLoading(true);
        try {
            const url = `${customBaseUrl.tranv2}/api/v1/admin/bulk-disbursement/${ref}`;
            const res = await httpGet(url);
            if (res.success) {
                setBatchRef(ref);
                setBatchData(res.data);
                setNarration(res.data.narration || "Bulk Disbursement");

                // Determine step based on status
                if (res.data.status === "UPLOADED") {
                    setCurrentStep(1);
                } else if (res.data.status === "VALIDATED") {
                    setCurrentStep(2);
                } else if (res.data.status === "PROCESSING" || res.data.status === "APPROVED") {
                    setCurrentStep(3);
                    startPolling();
                } else {
                    // COMPLETED or FAILED
                    setCurrentStep(3);
                }
            } else {
                notification.error({ message: res.message || "Failed to resume batch." });
            }
        } catch (error) {
            notification.error({ message: error.message || "An error occurred while resuming." });
        } finally {
            setLoading(false);
        }
    };

    const activeAccount = selectedAccount || manualAccount;

    const resetState = () => {
        setCurrentStep(0);
        setLoading(false);
        setBatchRef(null);
        setBatchData(null);
        setFile(null);
        setNarration("Bulk Disbursement");
        setOtp("");
        setPolling(false);
        setManualAccount(null);
        setFilterStatus("ALL");
    };

    const handleUpload = async () => {
        if (!activeAccount) {
            notification.error({ message: "Please select a GL account." });
            return;
        }
        if (!file) {
            notification.error({ message: "Please select a file to upload." });
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const url = `${customBaseUrl.tranv2}/api/v1/admin/bulk-disbursement/upload?glAccountCode=${activeAccount.accountCode}&narration=${narration}`;
            const res = await httpPostFormData(url, formData);

            if (res.success) {
                setBatchRef(res.data.batchReference);
                notification.success({ message: "File uploaded successfully!" });
                setCurrentStep(1);
            } else {
                notification.error({ message: res.message || "Upload failed." });
            }
        } catch (error) {
            notification.error({ message: error.message || "An error occurred during upload." });
        } finally {
            setLoading(false);
        }
    };

    const handleValidate = async () => {
        setLoading(true);
        try {
            const url = `${customBaseUrl.tranv2}/api/v1/admin/bulk-disbursement/${batchRef}/validate`;
            const res = await httpPost(url, {});

            if (res.success) {
                setBatchData(res.data);
                notification.success({ message: "Batch validated successfully!" });
                setCurrentStep(2);
            } else {
                notification.error({ message: res.message || "Validation failed." });
            }
        } catch (error) {
            notification.error({ message: error.message || "An error occurred during validation." });
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async () => {
        setLoading(true);
        try {
            const payload = {
                userId: localStorage.getItem("userId"),
                amount: batchData?.totalAmount || 0,
                transferReference: batchRef,
            };
            const res = await httpPost(`${customBaseUrl.authUrl}/otp/transfer/send`, payload);

            if (res.success) {
                notification.success({ message: "OTP sent successfully!" });
            } else {
                notification.error({ message: res.message || "Failed to send OTP." });
            }
        } catch (error) {
            notification.error({ message: error.message || "An error occurred while sending OTP." });
        } finally {
            setLoading(false);
        }
    };

    const downloadTemplate = () => {
        const headers = "Account Number,Amount,Narration";
        const blob = new Blob([headers], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.setAttribute("hidden", "");
        a.setAttribute("href", url);
        a.setAttribute("download", "bulk_disbursement_template.csv");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleApprove = async () => {
        if (!otp) {
            notification.error({ message: "Please enter the OTP." });
            return;
        }

        setLoading(true);
        try {
            const url = `${customBaseUrl.tranv2}/api/v1/admin/bulk-disbursement/${batchRef}/approve?otp=${otp}`;
            const res = await httpPost(url, {});

            if (res.success) {
                notification.success({ message: "Disbursement approved! Processing..." });
                setCurrentStep(3);
                startPolling();
            } else {
                notification.error({ message: res.message || "Approval failed." });
            }
        } catch (error) {
            notification.error({ message: error.message || "An error occurred during approval." });
        } finally {
            setLoading(false);
        }
    };

    const startPolling = () => {
        setPolling(true);
        const interval = setInterval(async () => {
            try {
                const url = `${customBaseUrl.tranv2}/api/v1/admin/bulk-disbursement/${batchRef}/poll-status`;
                const res = await httpPost(url, {});

                if (res.success) {
                    setBatchData(res.data);
                    if (res.data.status === "COMPLETED" || res.data.status === "FAILED") {
                        clearInterval(interval);
                        setPolling(false);
                        if (onSuccess) onSuccess();
                    }
                }
            } catch (error) {
                console.error("Polling error:", error);
            }
        }, 5000);
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Space direction="vertical" style={{ width: "100%" }} size="large">
                        {selectedAccount ? (
                            <Descriptions title="Source GL Details" bordered size="small">
                                <Descriptions.Item label="Account Name">{selectedAccount?.accountName}</Descriptions.Item>
                                <Descriptions.Item label="Account Code">{selectedAccount?.accountCode}</Descriptions.Item>
                            </Descriptions>
                        ) : (
                            <Form layout="vertical">
                                <Form.Item label="Select Source GL Account" required>
                                    <Select
                                        placeholder="Select GL account"
                                        showSearch
                                        optionFilterProp="children"
                                        onChange={(val) => {
                                            const acc = allAccounts.find(a => a.accountCode === val);
                                            setManualAccount(acc);
                                        }}
                                        filterOption={(input, option) => {
                                            const children = Array.isArray(option.children) ? option.children.join("") : option.children;
                                            return (children || "").toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                        }}
                                    >
                                        {allAccounts?.map(acc => (
                                            <Option key={acc.accountCode} value={acc.accountCode}>
                                                {acc.accountName} ({acc.accountCode})
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Form>
                        )}
                        <Form layout="vertical">
                            <Form.Item label="Narration" required>
                                <Input value={narration} onChange={(e) => setNarration(e.target.value)} />
                            </Form.Item>
                            <Form.Item
                                label="Select Disbursement File (Excel)"
                                required
                                extra={
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        onClick={downloadTemplate}
                                        style={{ padding: 0 }}
                                    >
                                        Download Template
                                    </Button>
                                }
                            >
                                <Upload
                                    beforeUpload={(file) => {
                                        setFile(file);
                                        return false;
                                    }}
                                    onRemove={() => setFile(null)}
                                    fileList={file ? [file] : []}
                                    accept=".xlsx, .xls, .csv"
                                >
                                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                                </Upload>
                            </Form.Item>
                            <Button type="primary" onClick={handleUpload} loading={loading} block>
                                Upload and Continue
                            </Button>
                        </Form>
                    </Space>
                );

            case 1:
                return (
                    <Result
                        status="info"
                        title="File Uploaded"
                        subTitle={`Batch Reference: ${batchRef}. Click below to validate the recipients.`}
                        extra={[
                            <Button type="primary" key="validate" onClick={handleValidate} loading={loading}>
                                Validate Batch
                            </Button>,
                        ]}
                    />
                );

            case 2:
                const filteredItems = batchData?.items?.filter(item => {
                    if (filterStatus === "VALIDATED") return item.validationStatus === "VALIDATED";
                    if (filterStatus === "INVALID") return item.validationStatus === "INVALID";
                    return true;
                }) || [];

                const validationColumns = [
                    { title: "Row", dataIndex: "rowIndex", key: "rowIndex", width: 60 },
                    { title: "Account Number", dataIndex: "accountNumber", key: "accountNumber" },
                    { title: "Account Name", dataIndex: "resolvedAccountName", key: "resolvedAccountName", render: (text) => text || "N/A" },
                    { title: "Amount", dataIndex: "amount", key: "amount", render: (val) => currencyFormat(val) },
                    {
                        title: "Status",
                        dataIndex: "validationStatus",
                        key: "validationStatus",
                        render: (status) => (
                            <Tag color={status === "VALIDATED" ? "green" : "red"}>{status}</Tag>
                        )
                    },
                    { title: "Message", dataIndex: "validationMessage", key: "validationMessage" },
                ];

                return (
                    <Space direction="vertical" style={{ width: "100%" }} size="large">
                        <Descriptions title="Batch Validation Summary" bordered size="small">
                            <Descriptions.Item label="Total Items">{batchData?.totalItems}</Descriptions.Item>
                            <Descriptions.Item label="Valid Items">
                                <Tag color="green">{batchData?.validatedItems}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Invalid Items">
                                <Tag color="red">{batchData?.invalidItems}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Total Amount">{currencyFormat(batchData?.totalAmount)}</Descriptions.Item>
                        </Descriptions>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold' }}>Validation Details</span>
                            <Radio.Group value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} size="small">
                                <Radio.Button value="ALL">All ({batchData?.items?.length || 0})</Radio.Button>
                                <Radio.Button value="VALIDATED">Valid ({batchData?.validatedItems || 0})</Radio.Button>
                                <Radio.Button value="INVALID">Invalid ({batchData?.invalidItems || 0})</Radio.Button>
                            </Radio.Group>
                        </div>

                        <Table
                            dataSource={filteredItems}
                            columns={validationColumns}
                            rowKey="rowIndex"
                            size="small"
                            pagination={{ pageSize: 5 }}
                            scroll={{ x: true }}
                        />

                        <Form layout="vertical">
                            <Form.Item label="Step 3: Administrative Approval" extra="Check your mobile/email for the transfer OTP">
                                <Space>
                                    <Button onClick={handleSendOtp} loading={loading}>Resend OTP</Button>
                                    <Input
                                        placeholder="Enter OTP"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        style={{ width: 200 }}
                                    />
                                </Space>
                            </Form.Item>
                            <Button type="primary" onClick={handleApprove} loading={loading} block disabled={!otp}>
                                Approve and Execute Disbursement
                            </Button>
                        </Form>
                    </Space>
                );

            case 3:
                return (
                    <Result
                        status={batchData?.status === "COMPLETED" ? "success" : "info"}
                        icon={polling ? <LoadingOutlined /> : (batchData?.status === "COMPLETED" ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />)}
                        title={polling ? "Processing Disbursement..." : `Disbursement ${batchData?.status}`}
                        subTitle={`Batch Reference: ${batchRef}. ${batchData?.successfulItems} items successful out of ${batchData?.totalItems}.`}
                        extra={[
                            <Button type="primary" key="done" onClick={() => { hideModal(); resetState(); }}>
                                Done
                            </Button>,
                            // <Button key="view" onClick={() => notification.info({ message: "Detailed report view coming soon" })}>
                            //     View Report
                            // </Button>
                        ]}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Modal
            title="GL Bulk Disbursement"
            visible={showModal}
            onCancel={() => { hideModal(); resetState(); }}
            footer={null}
            width={700}
            destroyOnClose
        >
            <Steps current={currentStep} size="small" style={{ marginBottom: 32 }}>
                <Step title="Upload" icon={<UploadOutlined />} />
                <Step title="Validate" icon={<CheckCircleOutlined />} />
                <Step title="Approve" icon={<CheckCircleOutlined />} />
                <Step title="Status" icon={<LoadingOutlined />} />
            </Steps>
            {renderStepContent()}
        </Modal>
    );
};

export default BulkDisbursementModal;
